import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { UploadFile } from "antd/lib/upload/interface";

import { ErrorCodeErrorDTO, FirmwareDTO, FirmwareType } from "@generated/v2";
import {
  readerversionDelete,
  readerVersionSave,
  requestUploadUrl,
  uploadVersionFile,
} from "app/api/v2/readerFirmwareApi";

type FirmwareUploadParam = Pick<FirmwareDTO, "version" | "description"> & {
  firmwareType: FirmwareType;
  file: UploadFile;
};

interface FirmwareUploadState {
  loading: boolean;
  upload: (params: FirmwareUploadParam) => Promise<void>;
}

export function useFirmwareUpload(): FirmwareUploadState {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const deleteFirmware = useCallback(
    async (id: number, firmwareType: FirmwareType) => {
      try {
        await readerversionDelete({ id, type: firmwareType });
      } catch (_) {
        // ignore the error
      }
    },
    []
  );

  const upload: FirmwareUploadState["upload"] = useCallback(
    async ({ version, description, firmwareType, file }) => {
      let id: number | undefined;
      try {
        setLoading(true);
        const { data: firmwareData } = await readerVersionSave({
          createFirmwareCommand: { version, description },
          type: firmwareType,
        });

        id = firmwareData.id;

        message.success(t("readerFirmware.createFirmwareSuccess"));

        const { data: urlData } = await requestUploadUrl({
          id: firmwareData.id,
          type: firmwareType,
        });

        await uploadVersionFile(file, urlData);

        message.success(t("readerFirmware.uploadFirmwareSuccess"));
      } catch (e: any) {
        const errorCodes: ErrorCodeErrorDTO[] | undefined = e?.response?.data;

        if (!!errorCodes?.length && "message" in errorCodes[0]) {
          message.error(errorCodes[0].message);
        } else {
          message.error(t("readerFirmware.uploadFirmwareError"));
        }

        // If an error happens after the creation of the firmware
        // and the file was not uploaded, we delete the firmware
        if (id) {
          deleteFirmware(id, firmwareType);
        }
      } finally {
        setLoading(false);
      }
    },
    [deleteFirmware, t]
  );

  return {
    loading,
    upload,
  };
}
